import { useResizeObserver } from '@react-aria/utils';
import clsx from 'clsx';
import { ForwardedRef, forwardRef, KeyboardEvent, ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { Button, ButtonContext, Label, PopoverContext, Selection, Text } from 'react-aria-components';
import IconButton from '~/components/buttons/NewIconButton';
import Icon from '~/components/data-display/Icon';
import fieldStyles from '~/components/inputs/Field.module.scss';
import { HStack } from '~/components/layout/Stack';
import useDataTestId from '~/hooks/useDataTestId';
import { useOnClickOutside } from '~/hooks/useOnClickOutside';
import ChevronIcon from '~/icons/icon-chevron-down.svg?react';
import IconClose from '~/icons/icon-close.svg?react';
import { ListBoxOptionEntry } from './ListBoxPopover';
import MultiListBoxPopover from './MultiListBoxPopover';
import { BaseSelectProps, SelectOption } from './Select';
import styles from './Select.module.scss';

export interface MultiSelectProps<T> extends Omit<BaseSelectProps, 'filterText' | 'filterPlaceholder' | 'filterLabel'> {
  clearable?: boolean;
  options: SelectOption<T>[];
  onChange: (value: T[]) => void;
  popoverClassName?: string;
  value: T[];
  singleSelectionVariant?: 'default' | 'compact';
  renderMultipleSelectionValue?: (options: SelectOption<T>[]) => string;
}

const EmptySelection: React.FC<{ placeholder?: string }> = ({ placeholder }) => (
  <div data-placeholder className={styles.value}>
    {placeholder}
  </div>
);

const SingleSelection = <T,>({
  option,
  variant,
}: {
  option: SelectOption<T>;
  variant: MultiSelectProps<T>['singleSelectionVariant'];
}) => (
  <>
    {variant === 'default' && option.Icon && <option.Icon className={styles.icon} />}
    {variant === 'default' && option.media ? <div className={styles.media}>{option.media}</div> : null}
    <div className={styles.value}>{option.selectedLabel ?? option.label}</div>
  </>
);

const MultipleSelection = <T,>({
  options,
  customRender,
}: {
  options: SelectOption<T>[];
  customRender: MultiSelectProps<T>['renderMultipleSelectionValue'];
}) => (
  <div className={styles.value}>
    {customRender
      ? customRender(options)
      : options.map((selectedOption) => selectedOption.selectedLabel ?? selectedOption.label)}
  </div>
);

const MultiSelect = <T,>(
  {
    size = 'medium',
    name,
    options,
    clearable,
    popoverClassName,
    singleSelectionVariant = 'default',
    ...props
  }: MultiSelectProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const dt = useDataTestId(props.dataTestId);

  const internalRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const selectedOptions = options.filter((el) => props.value.includes(el.value));
  const selectedKeysArray = new Set(selectedOptions.map((x) => x.key));

  const handleClickOutside = (target: EventTarget | null) => {
    if (!isOpen) return;
    if (target instanceof Node && popoverRef.current?.contains(target)) return;

    setIsOpen(false);
  };

  useOnClickOutside(internalRef, (ev) => {
    handleClickOutside(ev.target);
  });

  const [buttonWidth, setButtonWidth] = useState<string | null>(null);
  const onResize = useCallback(() => {
    if (triggerRef.current) {
      setButtonWidth(triggerRef.current.offsetWidth + 'px');
    }
  }, [triggerRef]);

  useResizeObserver({
    ref: triggerRef,
    onResize,
  });

  const listBoxOptions = useMemo(
    (): ListBoxOptionEntry[] =>
      options.map((option) => ({
        description: option.description,
        id: option.key,
        Icon: option.Icon,
        label: option.label,
        media: option.media,
        textValue: option.label,
        isDisabled: option.isDisabled,
      })),
    [options],
  );

  const handleSelectionChange = (selection: Selection) => {
    const selected = selection === 'all' ? listBoxOptions.map((el) => el.id) : [...selection];

    return props.onChange(options.filter((x) => selected.includes(x.key)).map((x) => x.value));
  };

  const handleButtonKeyDown = useCallback(
    (ev: KeyboardEvent) => {
      if (['ArrowDown'].includes(ev.code)) {
        if (!isOpen) {
          setIsOpen(!isOpen);
          return;
        }

        const options = popoverRef.current?.querySelectorAll('[role="option"]');
        const firstOption = options?.item(0);
        if (firstOption instanceof HTMLElement) {
          firstOption.focus();
        }
      }
    },
    [isOpen],
  );

  const handleTriggerPress = () => {
    setIsOpen(!isOpen);
  };

  const contextValue = useMemo(
    () => ({
      trigger: 'Select',
      triggerRef,
      style: { '--trigger-width': buttonWidth } as React.CSSProperties,
    }),
    [buttonWidth],
  );

  return (
    // This PopoverContext.Provider is a temporary workaround. We should refactor this component
    // to use ReactAria's Select component as the container instead of a plain div, which would handle
    // the context automatically without manual management.
    // Reference: https://react-aria.adobe.com/Select
    <PopoverContext.Provider value={contextValue}>
      <div
        ref={(node) => {
          internalRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        aria-label={props.hideLabel ? props.label : undefined}
        className={clsx(fieldStyles.field, styles.selectField, props.className)}
        data-size={size}
        id={props.id}
        data-disabled={props.isDisabled}
        data-invalid={props.isInvalid || props.errorMessage}
        {...(isOpen && { 'data-open': true })}
        data-required={props.isRequired}
        onBlur={props.onBlur}
        style={props.style}
        data-testid={dt()}
      >
        {!props.hideLabel && (
          <Label>
            {props.label} {props.isRequired ? ' *' : null}
          </Label>
        )}
        <div className={styles.triggerContainer}>
          <Button
            className={clsx(styles.valueContainer, styles.multiselectTrigger)}
            name={name}
            ref={triggerRef}
            onPress={handleTriggerPress}
            onBlur={(ev) => handleClickOutside(ev.relatedTarget)}
            onKeyDown={handleButtonKeyDown}
          >
            {selectedOptions.length === 0 && <EmptySelection placeholder={props.placeholder} />}
            {selectedOptions.length === 1 && (
              <SingleSelection option={selectedOptions[0]!} variant={singleSelectionVariant} />
            )}
            {selectedOptions.length > 1 && (
              <MultipleSelection options={selectedOptions} customRender={props.renderMultipleSelectionValue} />
            )}
            <HStack>
              {clearable && !props.isDisabled && props.value.length > 0 && (
                <Icon component={IconClose} className={clsx(styles.fakeClear)} />
              )}
              <Icon component={ChevronIcon} />
            </HStack>
          </Button>
          {clearable && !props.isDisabled && props.value.length > 0 && (
            <ClearButton onPress={() => handleSelectionChange(new Set())} />
          )}
        </div>
        {props.helpText && <Text slot="description">{props.helpText}</Text>}
        {props.errorMessage && <Text slot="errorMessage">{props.errorMessage}</Text>}

        <MultiListBoxPopover
          isOpen={isOpen}
          ref={popoverRef}
          triggerRef={triggerRef}
          offset={2}
          options={listBoxOptions}
          onOpenChange={setIsOpen}
          onSelectionChange={handleSelectionChange}
          selectedOptions={selectedKeysArray}
          listLabel={`${props.label} Options`}
          popoverClassName={popoverClassName}
          dataTestId={dt('popover')}
        />
      </div>
    </PopoverContext.Provider>
  );
};

const ClearButton = ({ onPress, disabled = false }: { onPress: () => void; disabled?: boolean }) => {
  return (
    /**
     * ButtonContext.Provider isolates the clear button from react-aria's Select context
     * to prevent it from being attached to the select's button functionality
     */
    <ButtonContext.Provider value={null}>
      <IconButton
        className={styles.clear}
        label="clear"
        iconClassName={styles.icon}
        Icon={IconClose}
        variant="ghost"
        size="small"
        disabled={disabled}
        onPressStart={onPress}
      />
    </ButtonContext.Provider>
  );
};

export default forwardRef(MultiSelect) as <T>(
  p: MultiSelectProps<T> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReactElement;
